/**
 * Unified geolocation service.
 *
 * Returns both the browser-permission observation (granted/denied/unavailable,
 * with coords only when granted) and a coords pair for the city autocomplete.
 * The observation always reflects the real browser-permission outcome. The
 * autocomplete coords fall back to IP geolocation so the nearby-city dropdown
 * still works when the browser fix is unavailable, but that IP fallback never
 * changes the recorded permission_status.
 */

import { requestBrowserGeoObservation } from './browserGeo.js';
import { getLocationByIp } from './ipGeo.js';

/**
 * @param {?function({permission_status: string, lat: ?number, lon: ?number, accuracy: ?number}): void} [onObservation]
 *        Invoked with the browser-permission outcome the moment it resolves,
 *        before the IP fallback runs, so callers can persist it without waiting
 *        on the (possibly slower) dropdown coordinates.
 * @returns {Promise<{observation: Object, coords: ?{lat: number, lon: number}}>}
 */
export async function requestLocation(onObservation) {
    const observation = await requestBrowserGeoObservation();
    if (typeof onObservation === 'function') {
        onObservation(observation);
    }

    let coords = null;
    if (observation.permission_status === 'granted' && observation.lat !== null && observation.lon !== null) {
        coords = { lat: observation.lat, lon: observation.lon };
    } else {
        coords = await requestIpLocation();
    }

    return { observation, coords };
}

/**
 * IP-based coordinates for the city dropdown only. Never throws, never affects
 * the recorded permission_status.
 * @returns {Promise<?{lat: number, lon: number}>}
 */
export async function requestIpLocation() {
    try {
        return await getLocationByIp();
    } catch (ipError) {
        return null;
    }
}
